// components/Home.js
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Title,
  Card,
  Tab,
  TabContainer,
  AnalyticalTable
} from '@ui5/webcomponents-react';
import BuyOrderList from './BuyOrderList';
import axiosInstance from '../utils/axiosInstance';
import ASNList from './ASNList';
import RampList from './virtualRamp/RampList';
import Layout from './Layout';
import { PieChart, Pie, Cell } from 'recharts';
import ScheduleList from './appoiment/ScheduleList';
import usePermission from '../hooks/usePermission';
import AccessDenied from './AccessDenied';
import PermissionGuard from './PermissionGuard';
import GrnList from './GrnList';
import useAutoTranslate from '../hooks/useAutoTranslate'; // Importar el hook de traducción
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';

function Home() {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [activePart, setActivePart] = useState('home');
  const [dashboardData, setDashboardData] = useState({
    asnPendingCount: 0,
    purchaseOrderCount: 0,
    deliveriesReadyCount: 0,
    efficiencyData: { efficiencyASNDelayed: 0, efficiencyASNontime: 0 },
    asnCount: 0,
    purchaseOrderCountFromAPI: 0
  });
  const [biReport, setBiReport] = useState(null);
  const [biLoading, setBiLoading] = useState(false);
  const [biReportList, setBiReportList] = useState([]);
  const [selectedBiReportIndex, setSelectedBiReportIndex] = useState(0);
  
  const location = useLocation();
  const { hasPermission } = usePermission();
  const prevLocationState = useRef(null);
  const { autoT } = useAutoTranslate(); // Usar el hook de traducción
 
  // Definimos los tabs disponibles con sus respectivos permisos (ahora traducidos)
  // `anyOf` => basta tener `listView` en alguno de los módulos listados
  // (la pestaña Órdenes es compartida por OC y OV).
  const availableTabs = [
    { id: 'home', label: autoT('Inicio'), permission: { module: 'home', action: 'listView' } },
    { id: 'buyorder', label: autoT('Ordenes'), permission: { anyOf: ['buyorder', 'salesorder'], action: 'listView' } },
    { id: 'notification', label: autoT('Notificaciones'), permission: { module: 'notification', action: 'listView' } },
    { id: 'virtualramp', label: autoT('Ramplas'), permission: { module: 'virtualramp', action: 'listView' } },
    { id: 'schedules', label: autoT('Agendamientos'), permission: { module: 'schedules', action: 'listView' } },
    { id: 'grn', label: autoT('Entregas'), permission: { module: 'grn', action: 'listView' } }
  ];

  const tabHasAccess = (tab) => {
    if (!tab.permission) return true;
    const action = tab.permission.action || 'listView';
    if (Array.isArray(tab.permission.anyOf)) {
      return tab.permission.anyOf.some((m) => hasPermission(m, action));
    }
    return hasPermission(tab.permission.module, action);
  };

  // Filtrar tabs basados en permisos
  const visibleTabs = availableTabs.filter(tabHasAccess);

  const canViewHome = hasPermission('home', 'listView');

  // Cargar datos del dashboard una vez al montar (solo si tiene permiso `home`)
  useEffect(() => {
    if (!canViewHome) return;
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
 
        // Obtener estado de documentos
        const statusResponse = await axiosInstance.get('dashboard/getStatusDocument?status=pending');
        
        // Obtener datos de eficiencia
        const efficiencyResponse = await axiosInstance.get('dashboard/getEfficiency');
        
        // Obtener conteos de ASN y PO
        const countsResponse = await axiosInstance.get('dashboard/getCountASNPO');
        
        setDashboardData({
          asnPendingCount: statusResponse.data.AsnPending || 5,
          purchaseOrderCount: statusResponse.data.PurchaseOrderforDelivery || 12,
          deliveriesReadyCount: statusResponse.data.GrnList || 8,
          efficiencyData: efficiencyResponse.data || { efficiencyASNDelayed: 25, efficiencyASNontime: 75 },
          asnCount: countsResponse.data.AsnCount || 42,
          purchaseOrderCountFromAPI: countsResponse.data.PurchaseOrderCount || 68
        });
      } catch (error) {
        console.error(autoT('Error cargando datos del dashboard:'), error);
        // Valores por defecto en caso de error
        setDashboardData({
          asnPendingCount: 5,
          purchaseOrderCount: 12,
          deliveriesReadyCount: 8,
          efficiencyData: { efficiencyASNDelayed: 25, efficiencyASNontime: 75 },
          asnCount: 42,
          purchaseOrderCountFromAPI: 68
        });
      }
    };
 
    fetchDashboardData();
  }, [canViewHome]);

  // Cargar reporte FarosBi desde la configuración del tenant
  useEffect(() => {
    if (!canViewHome) return;
    const loadBiReport = async () => {
      try {
        const userDataString = localStorage.getItem('userData');
        if (!userDataString) return;
        const { tenantId } = JSON.parse(userDataString);

        const configResponse = await axiosInstance.get(`tenant-configurations/${tenantId}`);
        if (configResponse.data.code !== 'SUCCESS') return;

        const farosBiConfig = configResponse.data.data?.systemSettings?.FAROSBI?.reports;
        if (!farosBiConfig || !farosBiConfig.reportId || !farosBiConfig.tenantId
            || !farosBiConfig.token || !farosBiConfig.baseUrl) return;

        setBiLoading(true);

        const baseUrlEnc = encodeURIComponent(farosBiConfig.baseUrl);
        const filtroEnc  = farosBiConfig.filtro   ? `&filtro=${encodeURIComponent(farosBiConfig.filtro)}`     : '';
        const criterioEnc= farosBiConfig.criterio ? `&criterio=${encodeURIComponent(farosBiConfig.criterio)}` : '';

        if (farosBiConfig.multipleReports) {
          const res = await axiosInstance.get(
            `farosbi/reports?tenantId=${farosBiConfig.tenantId}&token=${farosBiConfig.token}`
            + `&companyId=${farosBiConfig.companyId}&baseUrl=${baseUrlEnc}${filtroEnc}${criterioEnc}`
          );
          if (res.data.code === 'SUCCESS' && res.data.data?.towerPowerBi?.length > 0) {
            setBiReportList(res.data.data.towerPowerBi);
            setBiReport(res.data.data.towerPowerBi[0]);
          }
        } else {
          const res = await axiosInstance.get(
            `farosbi/reports/${farosBiConfig.reportId}?tenantId=${farosBiConfig.tenantId}&token=${farosBiConfig.token}`
            + `&baseUrl=${baseUrlEnc}${filtroEnc}${criterioEnc}`
          );
          if (res.data.code === 'SUCCESS' && res.data.data?.towerPowerBi?.length > 0) {
            setBiReport(res.data.data.towerPowerBi[0]);
          }
        }
      } catch (error) {
        console.error('Error cargando reporte FarosBi:', error);
      } finally {
        setBiLoading(false);
      }
    };
    loadBiReport();
  }, [canViewHome]);

  // Función para sincronizar el índice visual con el contenido actual
  const syncTabIndexWithContent = (selectedTab) => {
    // Si es un string, buscar su índice en visibleTabs
    if (typeof selectedTab === 'string') {
      const index = visibleTabs.findIndex(tab => tab.id === selectedTab);
      if (index !== -1) {
        return index;
      }
    }
    // Si es un número y está en rango
    else if (typeof selectedTab === 'number' && selectedTab >= 0 && selectedTab < visibleTabs.length) {
      return selectedTab;
    }
    // Por defecto, devolver 0 (Inicio)
    return 0;
  };

  // Efecto para manejar navegación desde otras partes de la app
  useEffect(() => {
    if (location.state && JSON.stringify(location.state) !== JSON.stringify(prevLocationState.current)) {
      console.log(autoT('Nuevo estado recibido:'), location.state);
      prevLocationState.current = location.state;
      
      const { activePart: newActivePart, selectedTab: newSelectedTab } = location.state;
      
      if (newActivePart) {
        setActivePart(newActivePart);
      }
      
      if (newSelectedTab !== undefined) {
        const tabIndex = syncTabIndexWithContent(newSelectedTab);
        console.log(autoT('Cambiando a tab: {{tab}} (índice visual: {{index}})', { 
          tab: newSelectedTab, 
          index: tabIndex 
        }));
        setSelectedTabIndex(tabIndex);
      }
    }
  }, [location.state, visibleTabs]);
 
  // Manejador simplificado para cambio de tabs
  const handleTabSelect = (event) => {
    const tabIndex = event.detail.tabIndex;
    console.log(autoT('Tab seleccionado manualmente:'), tabIndex);
    
    // Validamos que el índice esté dentro del rango de tabs visibles
    if (tabIndex >= 0 && tabIndex < visibleTabs.length) {
      setSelectedTabIndex(tabIndex);
    }
  };
 
  // Datos para los gráficos circulares
  const dataEficiencia = [
    { name: autoT('Notificación retrasados'), value: dashboardData.efficiencyData.efficiencyASNDelayed },
    { name: autoT('Notificación a tiempo'), value: dashboardData.efficiencyData.efficiencyASNontime }
  ];
 
  const dataOCvsASN = [
    { name: autoT('OC'), value: dashboardData.purchaseOrderCountFromAPI },
    { name: autoT('Notificación'), value: dashboardData.asnCount }
  ];
 
  const COLORS = ['#0088FE', '#00C49F'];
  
  // Función para renderizar el contenido basado en el tab seleccionado
  const renderTabContent = () => {
    // Asegurarse de que el tab seleccionado existe
    if (selectedTabIndex < 0 || selectedTabIndex >= visibleTabs.length) {
      return null;
    }
    
    const selectedTab = visibleTabs[selectedTabIndex];
    console.log(autoT('Renderizando contenido para tab: {{tab}} (índice: {{index}})', { 
      tab: selectedTab.id, 
      index: selectedTabIndex 
    }));
    
    switch (selectedTab.id) {
      case 'home':
        return (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '20px',
              marginBottom: '20px',
            }}>
              {/* Selector de reporte cuando hay múltiples */}
              {biReportList.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {biReportList.map((report, idx) => (
                    <button
                      key={report.id}
                      onClick={() => { setSelectedBiReportIndex(idx); setBiReport(report); }}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '4px',
                        border: '1px solid var(--sapHighlightColor)',
                        backgroundColor: idx === selectedBiReportIndex ? 'var(--sapHighlightColor)' : 'transparent',
                        color: idx === selectedBiReportIndex ? '#fff' : 'var(--sapHighlightColor)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                    >
                      {report.title}
                    </button>
                  ))}
                </div>
              )}

              <Card style={{ height: 'auto', overflow: 'hidden', margin: 0, padding: 0 }}>
                <div style={{ padding: '0', height: '75vh' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '75vh',
                    overflow: 'hidden'
                  }}>
                    {biLoading && (
                      <div style={{ color: 'var(--sapContent_LabelColor)' }}>
                        {autoT('Cargando reporte...')}
                      </div>
                    )}
                    {!biLoading && biReport && (
                      <PowerBIEmbed
                        embedConfig={{
                          type: 'report',
                          id: biReport.reportId,
                          embedUrl: biReport.finalEmbedUrl,
                          accessToken: biReport.embedToken,
                          tokenType: models.TokenType.Embed,
                          settings: {
                            panes: {
                              filters: { expanded: false, visible: false }
                            },
                            background: models.BackgroundType.Transparent,
                          }
                        }}
                        eventHandlers={new Map([
                          ['loaded',   () => console.log('FarosBi report loaded')],
                          ['rendered', () => console.log('FarosBi report rendered')],
                          ['error',    (event) => console.error('FarosBi report error:', event.detail)],
                        ])}
                        cssClassName="farosbi-report-embed"
                      />
                    )}
                    {!biLoading && !biReport && (
                      <div style={{ textAlign: 'center', color: 'var(--sapContent_LabelColor)' }}>
                        <p style={{ marginBottom: '8px' }}>
                          {autoT('No se ha configurado ningún reporte FarosBi.')}
                        </p>
                        <p style={{ fontSize: '0.9rem' }}>
                          {autoT('Ve a Configuración del Sistema → FAROSBI → Reportes para configurarlo.')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        );
      case 'buyorder':
        return <BuyOrderList key="buyorder-list" />;
      case 'notification':
        return <ASNList key="asn-list" />;
      case 'virtualramp':
        return <RampList key="ramp-list" />;
      case 'schedules':
        return <ScheduleList key="schedule-list" />;
      case 'grn':
        return <GrnList key="grn-list" />;
      default:
        return null;
    }
  };
 
  return (
    <Layout>
      {activePart === 'home' ? (
        <div>
          <TabContainer
            onTabSelect={handleTabSelect}
            selectedIndex={selectedTabIndex}
            fixed={false}
            collapsed={false}
            style={{ backgroundColor: 'white', marginBottom: '20px' }}
            key={`tab-container-${visibleTabs.length}-${selectedTabIndex}`}
          >
            {visibleTabs.map((tab) => (
              <Tab 
                key={tab.id} 
                text={tab.label} 
                selected={visibleTabs.indexOf(tab) === selectedTabIndex}
              />
            ))}
          </TabContainer>
 
          {/* Contenido del tab seleccionado con key única para forzar re-render */}
          <div key={`tab-content-${selectedTabIndex}`}>
            {renderTabContent()}
          </div>
        </div>
      ) : (
        /* Contenido según activePart */
        (() => {
          switch (activePart) {
            case 'BuyOrderList':
              return (
                <PermissionGuard requiredAnyOf={['buyorder', 'salesorder']} requiredAction="listView">
                  <BuyOrderList />
                </PermissionGuard>
              );
            case 'ASNList':
              return (
                <PermissionGuard requiredPermission="notification" requiredAction="listView">
                  <ASNList />
                </PermissionGuard>
              );
            case 'RampList':
              return (
                <PermissionGuard requiredPermission="virtualramp" requiredAction="listView">
                  <RampList />
                </PermissionGuard>
              );
            case 'ScheduleList':
              return (
                <PermissionGuard requiredPermission="schedules" requiredAction="listView">
                  <ScheduleList />
                </PermissionGuard>
              );
            case 'GrnList':
              return (
                <PermissionGuard requiredPermission="grn" requiredAction="listView">
                  <GrnList />
                </PermissionGuard>
              );
            default:
              return null;
          }
        })()
      )}
    </Layout>
  );
}
 
export default Home;